// Pure message parser for live SignalR race data.
// Adapted from announcerscreen/src/components/parseRaceMessages.jsx.
// No global state — receives currentState, returns a new state object.

import axios from "axios";
import {
  API_KEY_FETCH_RACE_DATA,
  API_RACE_CAR_COUNTS_URL
} from "./apiUrlList";

import { api } from "./apiClientWithCsrf";

const MARKERS = [
  "R1","R2","R3","R4","R5","R6","R7",
  "b1","b2","c1","c2","fl","fr","L+","R+",
  "L0","L1","L2","L3","L4","L5","L6","L7",
  "m1","m2","m-","n1","n2","q1","q2","w-",
  "round","category","TimeStamp","S5","S6","D5","D6",
  "raceCounter","raceDate",
];

const LABEL_LANE_MAP = {
  rt:     { left: "leftRt",      right: "rightRt"      },
  ft60:   { left: "leftFt60",    right: "rightFt60"    },
  ft330:  { left: "leftFt330",   right: "rightFt330"   },
  ft8:    { left: "leftft8",     right: "rightft8"     },
  ft8Mph: { left: "leftft8Mph",  right: "rightft8Mph"  },
  ft1000: { left: "leftft1000",  right: "rightft1000"  },
  ft4:    { left: "leftft4",     right: "rightft4"     },
  ft4Mph: { left: "leftft4Mph",  right: "rightft4Mph"  },
  first:  { left: "leftFirst",   right: "rightFirst"   },
  carNo:  { left: "leftCarNo",   right: "rightCarNo"   },
  qPos:   { left: "leftQPos",    right: "rightQPos"    },
};

export const INITIAL_RACE_STATE = {
  leftCarNo: "-",   leftDriver: "-",   leftCarClass: "-",
  leftDialIn: "-",  leftRt: "-",       leftFt60: "-",
  leftFt330: "-",   leftft8: "-",      leftft8Mph: "-",
  leftft1000: "-",  leftft4: "-",      leftft4Mph: "-",
  leftFirst: "-",   leftQPos: "-",     leftCurrentDialIn: "-" ,
  rightCarNo: "-",  rightDriver: "-",  rightCarClass: "-",
  rightDialIn: "-", rightRt: "-",      rightFt60: "-",
  rightFt330: "-",  rightft8: "-",     rightft8Mph: "-",
  rightft1000: "-", rightft4: "-",     rightft4Mph: "-",
  rightFirst: "-",  rightQPos: "-",    rightCurrentDialIn: "-",
  mov: "-",  win: "-",  category: "-",  round: "-",  timestamp: "-",
  raceStarted: false,
};

const TIMING_LABELS = new Set([
  "rt", "ft60", "ft330", "ft8", "ft8Mph", "ft1000", "ft4", "ft4Mph", "first",
]);

function buildTokenMap(leftLane, rightLane) {
  const map = {};
  if (!leftLane || !rightLane) return map;
  Object.entries(leftLane).forEach(([k, v]) => { map[k] = { lane: "left",  label: v }; });
  Object.entries(rightLane).forEach(([k, v]) => { map[k] = { lane: "right", label: v }; });
  return map;
}

function cleanValue(v) {
  return v.replace(/^[,\s]+/, "").replace(/[,\s]+$/, "");
}

function toFixed3(n) {
  const v = Number(n);
  return isNaN(v) ? "-" : v.toFixed(3);
}

function safeStr(s) {
  const v = String(s).trim();
  return v === "" || v === "NaN" ? "-" : v;
}

// Mirrors the preprocessing + splitting logic in parseRaceMessages.jsx
function splitIntoParts(rawMessage) {
  const NUMERIC_MARKERS = new Set(["fr", "fl"]);

  let preprocessed = rawMessage.toString().trim()
    .replace(/([A-Za-z0-9])(?=L\d)/g, "$1 ");

  const rawLines = preprocessed.split(/[\x01\n\r]+/).filter(Boolean);
  const segments = [];
  let buffer = "";
  for (const line of rawLines) {
    const clean = line.replace(/[\x00-\x1F\x7F]/g, "").trim();
    if (!clean) continue;
    if (/^S\d+:/.test(clean)) {
      if (buffer) segments.push(buffer.trim());
      buffer = clean;
    } else {
      buffer += " " + clean;
    }
  }
  if (buffer) segments.push(buffer.trim());

  const parts = [];
  for (const segment of segments) {
    // Keep S5:D.../S6:D... fully intact — don't let the marker loop below
    // shred it (it would otherwise find "D6"/"D5" inside the value and
    // split there, since those are also marker tokens).
    if (/^(S5|S6):D/.test(segment)) {
      parts.push(segment);
      continue;
    }

    let remaining = segment;
    while (remaining) {
      let splitIndex = -1;
      for (const marker of MARKERS) {
        let idx = remaining.indexOf(marker, 2);

        if (NUMERIC_MARKERS.has(marker)) {
          while (idx !== -1) {
            const nextChar = remaining[idx + marker.length];
            if (!nextChar || /[\d.\s]/.test(nextChar)) {
              break;
            }
            idx = remaining.indexOf(marker, idx + 1);
          }
        }

        if (idx !== -1 && (splitIndex === -1 || idx < splitIndex)) {
          splitIndex = idx;
        }
      }
      if (splitIndex !== -1) {
        const before = remaining.slice(0, splitIndex).trim();
        if (before) parts.push(before);
        remaining = remaining.slice(splitIndex).trim();
      } else {
        if (remaining.trim()) parts.push(remaining.trim());
        break;
      }
    }
  }
  return parts;
}

// Mirrors decodeTimeStamp in parseRaceMessages.jsx — resets most fields when
// a new race begins. On reconnect the server replays current state starting
// with a TimeStamp for the race we're already showing; wiping in that case
// erases accumulated lane data and the partial replay refills it unevenly,
// producing the split-lane Frankenstein state. Skip the reset when the
// timestamp matches what we already have.
function applyTimestamp(msg, state) {
  const ts =
    msg.slice(10, 21).trim() + " " +
    msg.slice(22, 24).trim() + ":" +
    msg.slice(25, 27).trim() + ":" +
    msg.slice(28, 30).trim();
  if (state.timestamp === ts) return state;
  return { ...INITIAL_RACE_STATE, timestamp: ts, category: state.category, round: state.round,
    leftCurrentDialIn: state.leftDialIn, rightCurrentDialIn: state.rightDialIn };
}

function getCarCount(category, round, setCarRunCount) {
  const eventId = localStorage.getItem("trackEventSelection")
    ? JSON.parse(localStorage.getItem("trackEventSelection")).eventId
    : " ";
  if (!category || category === "-" || !round || round === "-") return;
  const requestData = {
    category: category,
    round: round,
    eventId: eventId
  }
  api
    .post(API_RACE_CAR_COUNTS_URL, requestData, {
      withCredentials: true,
    })
    .then((res) => setCarRunCount(res.data))
    .catch((err) => console.error("[CarRunCount] fetch failed:", err));
}

// Mirrors decodeCategoryAndRound
function applyCategoryRound(msg, state, setCarRunCount) {
  const first  = msg.indexOf(",");
  const second = msg.indexOf(",", first + 1);
  if (first < 0 || second < 0) return state;
  const category = msg.slice(first + 1, second).trim();
  const round    = msg.slice(second + 1).trim();
  getCarCount(category, round, setCarRunCount);
  return { ...state, category, round };
}

function convertNewDeviceDialIn(value) {
    if (value == null) return null;

    let s = String(value).trim();

    if (!s) return null;

    // Already contains decimal
    if (s.includes(".")) {
        return Number(s);
    }

    // REMOVE DIGIT AT POSITION -2 (the "2")
    if (s.length > 1) {
        s = s.slice(0, -2) + s.slice(-1);
    }

    // If result > 3 digits: skip first digit
    if (s.length > 3) {
        s = s.slice(1);
    }

    // Insert decimal before last 2 digits
    if (s.length > 2) {
        s = s.slice(0, -2) + "." + s.slice(-2);
    }

    const numValue = Number(s);

    // VALIDATION: Check if dial-in is in valid range (0.5 - 20 seconds)
    if (isNaN(numValue) || numValue < 0.5 || numValue > 20) {
        return null;
    }

    return numValue;
}


// Mirrors decodeLeftDialInTime
function applyDialInLeft(value, state) {
  const next = { ...state };

   // New device format
  if (value.trim().startsWith("#@,_1")) {
    let dialValue = value
      .replace("#@,_1", "")
      .replace("D0$", "")
      .trim();

    // Remove any spaces
    dialValue = dialValue.replace(/\s+/g, "");

    const convertedValue = convertNewDeviceDialIn(dialValue);
    next.leftDialIn = convertedValue !== null ? convertedValue : "-";
    return next;
  }

   
  const rightToken = value.includes("S6") ? "S6" : value.includes("D6") ? "D6" : null;
  if (rightToken) {
    const [s5Raw, s6Raw] = value.split(rightToken);
    const s5 = s5Raw.replace(",", "").trim();
    const s6 = s6Raw.replace(/[\x00-\x1F\x7F]+$/, "").trim();
    next.leftDialIn  = convertDialIn(extractNumeric(s5));
    next.rightDialIn = convertDialIn(extractNumeric(s6));
    return next;
  }
  next.leftDialIn = convertDialIn(extractNumeric(value));
  return next;
}

function applyDialLeftSpecialCase(value, state) {
    const next = { ...state };


  // ===== NEW FORMAT SUPPORT =====
  //#@,_110622 D0$
  //#@,_2 8327 D0$
  const trimmedValue = value.trim();

  const leftMatch = trimmedValue.match(/#@,_1\s*([\d.]+)/);

  if (leftMatch) {
    let dialValue = leftMatch[1].trim();

    // Old device sends 110622 -> use last 4 digits
    if (!dialValue.includes(".") && dialValue.length > 4) {
      dialValue = dialValue.slice(-4);
    }

    next.leftDialIn = convertDialIn(dialValue);
    return next;
  }
}
  

function extractNumeric(v) {
  const m = String(v).match(/-?\d+\.?\d*/);
  return m ? m[0] : "";
}

function convertDialIn(value) {
    // If the input is null, undefined, or not a string, handle safely
    if (value === null || value === undefined) return null;

    
    const stringValue = String(value);
    const isDecimal = stringValue.includes(".");

    // Convert to a number. If it's empty or invalid, Number() returns NaN.
    const numericValue = Number(stringValue);
    
    // Check if the conversion was successful (not NaN)
    if (!isNaN(numericValue) && stringValue.trim() !== "") {
        if (!isDecimal) {
            return parseFloat((numericValue * 0.01).toFixed(3));
        }
        return numericValue;
    }
    
    return null; // Return null to mirror Java's handling of invalid/null inputs
}

// Mirrors decodeRightDialInTime
function applyDialInRight(value, state) {
  const next = { ...state };


   // New device format
  if (value.trim().startsWith("#@,_2")) {
    let dialValue = value
      .replace("#@,_2", "")
      .replace("D0$", "")
      .trim();

    // Remove spaces
    dialValue = dialValue.replace(/\s+/g, "");


    const convertedValue = convertNewDeviceDialIn(dialValue);
    next.rightDialIn = convertedValue !== null ? convertedValue : "-";
    return next;
  }



  if (value.includes("L")) {
    next.rightDialIn = convertDialIn(extractNumeric(value.split("L")[0]));
    next.leftRt = value.split("L")[1].trim();
  } else if (value.includes("R")) {
    next.rightDialIn = convertDialIn(extractNumeric(value.split("R")[0]));
    next.rightRt = value.split("R")[1].trim();
  } else {
    next.rightDialIn = convertDialIn(extractNumeric(value));
  }
  return next;
}

function applyDialRightSpecialCase(value, state) {
   const next = { ...state };



  // ===== NEW FORMAT SUPPORT =====
  //#@,_110622 D0$
  //#@,_2 8327 D0$
  const trimmedValue = value.trim();

  const rightMatch = trimmedValue.match(/#@,_2\s*([\d.]+)/);

  if (rightMatch) {
    let dialValue = rightMatch[1].trim();

    // Example: 8327 -> 837 (only if this is your device rule)
    if (!dialValue.includes(".") && dialValue.length === 4) {
      dialValue = dialValue[0] + dialValue.slice(2);
    }

    next.rightDialIn = convertDialIn(dialValue);
    return next;
  }
}

// Mirrors decodeSingleMessage — processes one token-value part
function applyPart(part, tokenMap, state, setCarRunCount) {

    const raw = part.trim();
    // Handle new device messages BEFORE cleaning
  if (raw.startsWith("#@,_1")) {
    return applyDialInLeft(raw, state);
  }

  if (raw.startsWith("#@,_2")) {
    return applyDialInRight(raw, state);
  }

  // "S5:D6.64" / "S6:D6.64" kept intact by splitIntoParts — pull the value
  // out directly instead of the generic 2-char token slicing below.
  if (raw.startsWith("S5:D")) {
    return applyDialInLeft(raw.slice(4).trim(), state);
  }
  if (raw.startsWith("S6:D")) {
    return applyDialInRight(raw.slice(4).trim(), state);
  }

  const clean = part.replace(/^[^a-zA-Z0-9]*/, "");
  if (!clean) return state;
  // Handle new dial-in messages
if (clean.startsWith("#@,_1")) {
  return applyDialInLeft(clean, state);
}

if (clean.startsWith("#@,_2")) {
  return applyDialInRight(clean, state);
}


  if (clean.includes("TimeStamp:")) return applyTimestamp(clean, state);
  if (clean.startsWith("C") && clean.indexOf(",") > 0) return applyCategoryRound(clean, state, setCarRunCount);

  const token = clean.slice(0, 2);
  const value = clean.slice(2);
  const tokenInfo = tokenMap[token];
  if (value === "") return state;
  if (!tokenInfo) {
    if (token === "D5") {  return { ...state, leftDialIn: convertDialIn(extractNumeric(value)) }; }
    if (token === "D6") {  return { ...state, rightDialIn: convertDialIn(extractNumeric(value)) }; }
    return state;
  }

  const { lane, label } = tokenInfo;
  const next = { ...state };


  if (label === "S5" || label === "D5") {
    return applyDialInLeft(value, state);
  }
  if (label === "S6" || label === "D6") {
    return applyDialInRight(value, state);
  }

  if (label === "mov") {
    next.mov = cleanValue(value);
    next.raceStarted = true;
    return next;
  }
  if (label === "driver") {
    if (lane === "left") next.leftDriver  = cleanValue(value);
    else                 next.rightDriver = cleanValue(value);
    return next;
  }
  if (label === "carClass") {
    if (lane === "left") next.leftCarClass  = cleanValue(value);
    else                 next.rightCarClass = cleanValue(value);
    return next;
  }
  if (label === "win") {
    next.win = cleanValue(value);
    next.raceStarted = true;
    return next;
  }

  if (LABEL_LANE_MAP[label]) {
    const key = LABEL_LANE_MAP[label][lane];
    if (key) next[key] = cleanValue(value);
    if (TIMING_LABELS.has(label)) next.raceStarted = true;
    return next;
  }

  return state;
}

export function parseMessage(rawMessage, leftLaneMap, rightLaneMap, currentState, setCarRunCount) {
  if (!rawMessage || !leftLaneMap || !rightLaneMap) return currentState;
  const tokenMap = buildTokenMap(leftLaneMap, rightLaneMap);
  const parts = splitIntoParts(rawMessage);
  return parts.reduce((state, part) => applyPart(part, tokenMap, state, setCarRunCount), currentState);
}
